import React, { useState, useEffect, useCallback } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import StampPop from "./StampPop";
import VideoList from "./VideoList";
import "./Timestamp.css";

// const apiKey = "AIzaSyBa1mRsdRyENmXGjcoE1p-O35xWDOd7D2c"; // moved to env
const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;

const youtubeRegex =
  /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/|m\.youtube\.com\/watch\?v=)(?<id>[\w-]{11})(&.*)?$/;

const extractVideoId = (url) => {
  const match = url.match(youtubeRegex);
  return match?.groups?.id || null;
};

const Timestamp = () => {
  const [url, setUrl] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [videoData, setVideoData] = useState(null);
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingTimestamps, setIsGeneratingTimestamps] = useState(false);

  // NEW: popup data
  const [stampData, setStampData] = useState(null);
  // Keep timestamp data for VideoList even after popup closes - persists across URL changes
  const [timestampDataForList, setTimestampDataForList] = useState(null);
  // Keep video data for VideoList - persists across URL changes
  const [storedVideoData, setStoredVideoData] = useState(null);

  // Load data from localStorage on component mount
  useEffect(() => {
    try {
      const latestData = localStorage.getItem("timestamp_latest");
      if (latestData) {
        const parsed = JSON.parse(latestData);
        console.log("Loading from localStorage:", parsed);
        
        // Restore timestamp data
        if (parsed.data) {
          setTimestampDataForList(parsed.data);
        }
        
        // Restore video data (title and thumbnail)
        if (parsed.videoData) {
          setVideoData(parsed.videoData);
          // Also store for VideoList persistence
          setStoredVideoData(parsed.videoData);
        }
        
        // Restore URL
        if (parsed.url) {
          setUrl(parsed.url);
        }
      }
    } catch (error) {
      console.error("Error loading data from localStorage:", error);
    }
  }, []); // Run only on mount

  const fetchVideoDetails = useCallback(async (videoId) => {
    if (!apiKey) {
      setStatus("Missing API key. Please set REACT_APP_YOUTUBE_API_KEY.");
      return;
    }

    setIsLoading(true);
    setStatus("");

    try {
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoId}&key=${apiKey}`
      );

      if (!response.ok) {
        throw new Error("Unable to fetch video details");
      }

      const data = await response.json();
      const snippet = data.items?.[0]?.snippet;

      if (!snippet) {
        setStatus("Could not find that video. Double-check the URL.");
        setVideoData(null);
        return;
      }

      const thumbnails = snippet.thumbnails || {};
      const thumbnailUrl =
        thumbnails.maxres?.url ||
        thumbnails.standard?.url ||
        thumbnails.high?.url ||
        thumbnails.medium?.url ||
        thumbnails.default?.url ||
        "";

      setVideoData({
        title: snippet.title,
        thumbnailUrl,
      });
    } catch (err) {
      setStatus("Something went wrong fetching the video. Please try again.");
      setVideoData(null);
    } finally {
      setIsLoading(false);
    }
  }, [apiKey]);

  const handleUrlChange = (e) => {
    const inputUrl = e.target.value;
    setUrl(inputUrl);
    // Only clear the preview videoData, not the stored data for VideoList
    setVideoData(null);
    setStatus("");
    // Don't clear timestampDataForList or storedVideoData - keep VideoList visible
    setStampData(null);

    if (inputUrl.trim() === "") {
      setIsValid(true);
      return;
    }

    const isValidUrl = youtubeRegex.test(inputUrl);
    setIsValid(isValidUrl);
  };

  // Auto-fetch video details when a valid URL is detected
  useEffect(() => {
    if (!url || url.trim() === "") {
      return;
    }

    if (!youtubeRegex.test(url)) {
      return;
    }

    const videoId = extractVideoId(url);
    if (!videoId) {
      return;
    }

    // Debounce: wait a bit after user stops typing
    const timeoutId = setTimeout(() => {
      fetchVideoDetails(videoId);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [url, fetchVideoDetails]);

  const handleGenerate = () => {
    if (url.trim() === "") {
      setIsValid(false);
      return;
    }

    if (!youtubeRegex.test(url)) {
      setIsValid(false);
      return;
    }

    const videoId = extractVideoId(url);

    if (!videoId) {
      setIsValid(false);
      setStatus("Could not extract a video ID from that URL.");
      return;
    }

    fetchVideoDetails(videoId);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (videoData) {
        handleGenerateTimestamps();
      } else {
        handleGenerate();
      }
    }
  };

  const handleGenerateTimestamps = async () => {
    if (!url || url.trim() === "") {
      setStatus("Please enter a YouTube URL first.");
      return;
    }

    setIsGeneratingTimestamps(true);
    setStatus("Generating timestamps...");

    try {
      const generateTimestamps = httpsCallable(functions, "generate_timestamps");
      const result = await generateTimestamps({ url });

      console.log("Timestamps data received:", result.data);

      // Store data in localStorage before displaying
      const timestampData = result.data;
      const videoId = extractVideoId(url);
      
      // Store with video ID as key for easy retrieval
      if (videoId) {
        localStorage.setItem(`timestamp_${videoId}`, JSON.stringify(timestampData));
      }
      
      // Create video entry with all data
      const videoEntry = {
        url,
        videoId,
        data: timestampData,
        videoData: videoData, // Include title and thumbnail
        timestamp: new Date().toISOString()
      };
      
      // Store as latest entry
      localStorage.setItem("timestamp_latest", JSON.stringify(videoEntry));
      
      // Add to history list (store all videos)
      try {
        const historyKey = "timestamp_history";
        const existingHistory = localStorage.getItem(historyKey);
        let history = existingHistory ? JSON.parse(existingHistory) : [];
        
        // Check if this video already exists in history (by videoId)
        const existingIndex = history.findIndex(item => item.videoId === videoId);
        
        if (existingIndex >= 0) {
          // Update existing entry
          history[existingIndex] = videoEntry;
        } else {
          // Add new entry to the beginning of the array
          history.unshift(videoEntry);
        }
        
        // Limit history to last 50 videos to prevent localStorage from getting too large
        if (history.length > 50) {
          history = history.slice(0, 50);
        }
        
        localStorage.setItem(historyKey, JSON.stringify(history));
      } catch (error) {
        console.error("Error saving to history:", error);
      }

      // Now display the data to the user
      console.log("Setting stampData:", timestampData);
      console.log("Current videoData:", videoData);
      setStampData(timestampData);
      // Also store for VideoList (persists even after popup closes and URL changes)
      setTimestampDataForList(timestampData);
      // Store video data for VideoList persistence
      if (videoData) {
        setStoredVideoData(videoData);
      }
      setStatus("");
    } catch (error) {
      console.error("Error generating timestamps:", error);
      setStatus(`Error: ${error.message || "Failed to generate timestamps"}`);
    } finally {
      setIsGeneratingTimestamps(false);
    }
  };

  return (
    <>
      <div className="timestamp">
        <p className="timestamp-desc">
          Paste a YouTube link to preview the video and generate smart timestamps.
        </p>

        <div className="timestamp-container">
          <input
            type="text"
            className={`timestamp-input ${
              !isValid ? "timestamp-input-invalid" : ""
            }`}
            placeholder="Paste your YouTube link here..."
            value={url}
            onChange={handleUrlChange}
            onKeyPress={handleKeyPress}
            aria-label="YouTube URL"
          />
          <div className="timestamp-button-wrapper">
            <span 
              className={`timestamp-hammer ${isGeneratingTimestamps ? 'hammering' : ''}`}
              role="img"
              aria-label="Hammer"
            >
              🔨
            </span>
            <button
              className="timestamp-button"
              onClick={videoData ? handleGenerateTimestamps : handleGenerate}
              disabled={isLoading || isGeneratingTimestamps}
            >
              {isGeneratingTimestamps
                ? "Generating..."
                : isLoading
                ? "Loading..."
                : videoData
                ? "Generate Timestamps"
                : "Generate"}
            </button>
          </div>
        </div>

        {!isValid && url.trim() !== "" && (
          <p className="timestamp-error">Please enter a valid YouTube URL</p>
        )}

        {status && <p className="timestamp-status">{status}</p>}

        {(isLoading || videoData) && (
          <div className="timestamp-preview">
            {isLoading ? (
              <div className="timestamp-thumbnail loading">
                <div className="timestamp-loading-spinner"></div>
                <p className="timestamp-loading-text">Loading video details...</p>
              </div>
            ) : videoData.thumbnailUrl ? (
              <img
                className="timestamp-thumbnail"
                src={videoData.thumbnailUrl}
                alt={videoData.title}
              />
            ) : (
              <div className="timestamp-thumbnail placeholder">
                No thumbnail
              </div>
            )}
            {videoData && !isLoading && (
              <p className="timestamp-title">{videoData.title}</p>
            )}
          </div>
        )}
      </div>

      {/* NEW: Timestamp popup */}
      {stampData && (
        <StampPop
          data={stampData}
          onClose={() => setStampData(null)}
        />
      )}

      {/* VideoList component - always render, it will load from localStorage if props are missing */}
      <VideoList
        timestampData={timestampDataForList}
        thumbnail={storedVideoData?.thumbnailUrl}
        title={storedVideoData?.title}
      />
    </>
  );
};

export default Timestamp;
